import debounce from 'lodash/debounce';
import React, { useState, FC, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { appDataModel } from '@entities/app-data';
import { docModel } from '@entities/doc';
import { CKEditorCS } from '@entities/ck-editor';
import { bookTypesDictionary, dictionary, MAX_TITLE_LENGTH } from '@shared/config';

import { Doc, CKEditorInstance } from '@shared/types';
import { EditorPageContentWrapper, ErrorBlock, Loader, Textarea } from '@shared/ui';
import { createNotification, snackbarModel } from '@features/snackbar';

import cn from 'classnames';
import { apaSectionExample } from './const';

type Props = {
  doc: Doc;
};

export const DocPageBase: FC<Props> = ({ doc }) => {
  const dispatch = useDispatch();
  const book = useSelector(appDataModel.selectors.selectedBookWithError);
  const isOwner = useSelector(appDataModel.selectors.selectIsBookOwnerWithError);
  const isApaPaper = book.type === 'apa';
  const example = isApaPaper ? apaSectionExample : '';

  const titleRef = React.createRef<HTMLTextAreaElement>();
  const [error, setError] = useState<Error | null>(null);

  const titlePlaceholder = isApaPaper ? undefined : `Untitled ${bookTypesDictionary[book.type].docName}`;
  const defaultTitle = isApaPaper && !doc.title ? 'Full Title of the Paper' : doc.title;

  const handleSaveDocTitle = (content: string, bookId: string, docId: string) => {
    if (content.length > MAX_TITLE_LENGTH) {
      dispatch(
        snackbarModel.actions.addNotificationAction(
          createNotification('primary', dictionary.primary.tooLongChapterName)
        )
      );
    }
    dispatch(
      docModel.actions.saveDocDetailsAction({
        content,
        bookId,
        id: docId,
        fieldName: 'title',
      })
    );
  };

  //eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleSaveDocTitle = useCallback(debounce(handleSaveDocTitle, 500), []);

  const handleChangeDocTitle = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    debouncedHandleSaveDocTitle(e.target.value, book.id, doc.id);
  };

  const editorRef = useRef<CKEditorInstance>();
  useEffect(() => {
    return () => {
      editorRef.current = null;
    };
  }, []);

  const focusEditor = () => {
    editorRef.current?.editing.view.focus();
  };

  const onTitlePress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      focusEditor();
    }
  };

  useEffect(() => {
    isOwner && handleSaveDocTitle(defaultTitle, book.id, doc.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTitle, doc.id, book.id, isOwner]);

  if (error) {
    return <ErrorBlock error={error} />;
  }

  return (
    <EditorPageContentWrapper>
      <Textarea
        ref={titleRef}
        className={cn(`flex-none px-10 py-0 text-center`, {
          'text-l font-semibold': !isApaPaper,
          'text-base leading-8 font-bold': isApaPaper,
        })}
        name='title'
        placeholder={titlePlaceholder}
        disabled={!isOwner}
        defaultValue={defaultTitle}
        key={`${doc.id}-title`}
        onChange={e => handleChangeDocTitle(e)}
        onKeyPress={onTitlePress}
      />

      <CKEditorCS
        bookId={book.id}
        onReady={editor => {
          editorRef.current = editor;

          if (doc.title) editor.editing.view.focus();
          else titleRef.current?.focus();
        }}
        documentHash={doc.id}
        onError={(e: Error) => {
          setError(e);
        }}
        example={example}
        section='content'
      />
    </EditorPageContentWrapper>
  );
};

export const DocPage = () => {
  const doc = useSelector(appDataModel.selectors.selectedDoc);
  return doc ? <DocPageBase doc={doc} /> : <Loader />;
};
